import type { Except } from 'type-fest'

import { InjectionKey, Ref } from 'vue'

import { Customer, Location, CustomerCompany, Company } from '@/types/company'
import { EntityLog, FieldPreference, MinimalResource, Model, uuid } from '@/types/general'
import { MinimalUser } from '@/types/user'

export interface Product extends Model {
    id: string
    image: string
    name: string
    description?: string
    dimensions?: string
    default: boolean
    category: string
}

export interface CompanyProductData {
    default: boolean
    price: number
    order: number
    externalProductId: string | null
    bottomPalletFraction: number | null
}

export interface CompanyProduct extends Product, CompanyProductData {}

export interface ProductBalanceRow {
    productId: string
    owed: number
    owes: number
    balance: number
}

export interface LocationBalanceRow {
    location: {
        id: uuid
        name: string
        address: string
        country: string
        zipcode: string
        city: string
        pNumber: string
        deleted: boolean
    }
    owed: number
    owes: number
    balance: number
}

export interface Statement {
    id: uuid
    company: Except<Company, 'locations'>
    receiverCompany: Except<Company, 'locations'>
    user?: MinimalUser
    companyLocations: Location[]
    companyLocationIds: uuid[]
    receiverLocations: Location[]
    receiverLocationIds: uuid[]
    startDate: string
    endDate: string
    transactionCount: number
    products: StatementProduct[]
    dispatches: StatementDispatch[]
    statementCompanyLocationFilterEnabled: boolean
}

export interface StatementDispatch {
    id: uuid
    user?: MinimalUser
    emails: string
    createdAt: string
}

export interface MinimalStatement extends Model {
    id: uuid
    user: MinimalUser
    company: Except<Company, 'locations'>
    receiverCompany: Except<Company, 'locations'>
    companyLocations: MinimalResource[]
    receiverLocations: MinimalResource[]
    startDate: string
    endDate: string
    transactionCount: number
    productCount: number
}

export interface StatementProduct {
    id: uuid
    name: string
    image: string
    primoDelivered: number
    primoReceived: number
    periodDelivered: number
    periodReceived: number
}

export enum BalanceLimitType {
    Amount = 0,
    Price = 1,
}

export interface CustomerBalanceLimit {
    productId: uuid | null
    type: BalanceLimitType
    upperLimit: number | null
    lowerLimit: number | null
    reachedAt: string | null
}

export enum ScheduleFrequency {
    Monthly = 'monthly',
    Weekly = 'weekly',
    every14days = 'every14days',
}

export interface ScheduledStatementOptions {
    frequency: ScheduleFrequency
    emails: string[]
    dayOfMonth: number
    packagingBalancePrice?: number
}

export interface PackagingPreferences {
    signature: FieldPreference
    signatureScanner: FieldPreference
    consignmentNote: FieldPreference
    shipmentNumber: FieldPreference
    note: FieldPreference
    truckNumber: FieldPreference
    trailerNumber: FieldPreference
    defaultProductSort: DefaultProductSort
    productPageStartTab: ProductPageStartTab
    transitLocationTrades: boolean
    nilTradesEnabled: boolean
    palletReceiptNumber: FieldPreference
    createCustomersInApp: boolean
    statementCompanyLocationFilterEnabled: boolean
    haulierLoadUnloadEnabled: boolean
    nonMypalletReceiverConfirmationEmails: boolean
    requireNoteOnProductDifference: boolean
    customAppRoleProductsInformationIcon: boolean
    bottomPalletHandling: boolean
}

export interface PackagingOrder extends Model {
    id: uuid
    companyId: uuid
    transactionId: uuid | null
    customerCompany: CustomerCompany
    customerLocation: Location | null
    supplierCompany: Company
    supplierLocation: Location | null
    orderNumber: string | null
    type: number
    status: number
    products: PackagingOrderProduct[]
    logs: EntityLog[]
    createdAt: string
    updatedAt: string
    plannedAt: string
}

export interface MinimalPackagingOrder extends Model {
    id: uuid
    customerCompany: CustomerCompany
    customerLocation: Location | null
    supplierCompany: Company
    supplierLocation: Location | null
    orderNumber: string | null
    type: number
    status: number
    createdAt: string
    updatedAt: string
    plannedAt: string
}

export interface PackagingOrderProduct extends Model {
    id: uuid
    // Only present when id is the pivot ID (not needed when id is already the product ID)
    productId?: uuid
    image: string
    name: string
    received: number
    delivered: number
}

export enum PackagingOrderType {
    Delivery = 0,
    Pickup = 1,
}

export enum PackagingOrderStatus {
    Created = 0,
    Accepted = 1,
    Finished = 2,
    Canceled = 3,
}

export enum DefaultProductSort {
    Custom = 0,
    ByUsage = 1,
}

export enum ProductPageStartTab {
    Delivered = 0,
    Received = 1,
}

export const productsOpenKey: InjectionKey<Ref<boolean>> = Symbol('productsOpen')
export const currentCustomerKey: InjectionKey<Ref<Customer<CustomerCompany> | undefined>> =
    Symbol('currentCustomer')
export const currentCustomerLocationKey: InjectionKey<Ref<Location | undefined>> =
    Symbol('currentCustomerLocation')
